import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../ui/button';
import { DEMO_CALL_MESSAGE } from '../../lib/leads/contactState';
import type { LeadSplitApi } from './LeadSplitView';

// "WHICH PHONE SHOULD WE RING FIRST?" (Leads centre v2, Part B). An inline panel under the lead's header (not a portaled modal, so it wears
// the host's look). It reads the options ONLY once it is open (it mounts when a person presses Call) and starts a call ONLY on a click.
// ⚠️ It names people, never numbers. The server decides everything: this panel shows the server's own sentence when a call is refused.

export interface CallLeadPanelProps {
  leadId: string;
  firstName: string;
  api: Pick<LeadSplitApi, 'callOptions' | 'startCall'>;
  onClose: () => void;
  onStarted?: () => void;
  /** A demo lead: the dialog opens, and pressing the button answers on the screen without calling the server (nothing is dialled). */
  demo?: boolean;
}

export function CallLeadPanel({ leadId, firstName, api, onClose, onStarted, demo = false }: CallLeadPanelProps) {
  const options = useQuery({ queryKey: ['lead-call-options', leadId], queryFn: () => api.callOptions(leadId), retry: false, staleTime: 0, gcTime: 0 });
  const [picked, setPicked] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [started, setStarted] = useState<string | null>(null);
  const [demoNote, setDemoNote] = useState(false);

  const data = options.data;
  const choice = picked ?? data?.defaultTeamMemberId ?? null;

  const start = async () => {
    if (!choice || !data) return;
    if (demo) {
      setDemoNote(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.startCall(leadId, choice);
      setStarted(data.staff.find((s) => s.teamMemberId === choice)?.name ?? 'your team member');
      onStarted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The call could not be started.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section role="dialog" aria-label="Which phone should we ring first?" className="kc-card space-y-3 rounded border border-border bg-card p-4" data-testid="call-panel">
      <div className="flex items-start justify-between gap-2">
        <h3 className="kc-display text-lg font-semibold text-foreground">Which phone should we ring first?</h3>
        <Button type="button" variant="ghost" size="sm" onClick={onClose} data-testid="call-panel-close">Close</Button>
      </div>
      {started ? (
        <p role="status" className="kc-body text-sm text-foreground" data-testid="call-started">
          Ringing {started} now. Press any key on that phone to connect, and we will dial {firstName} from the club&apos;s number.
        </p>
      ) : options.isLoading ? (
        <p className="kc-body text-sm text-muted-foreground">Checking…</p>
      ) : options.error || !data ? (
        <p role="alert" className="kc-body text-sm text-destructive" data-testid="call-options-error">
          {options.error instanceof Error ? options.error.message : 'We could not load the call options.'}
        </p>
      ) : data.unavailable && !(demo && data.unavailable.code === 'demo') ? (
        <p role="status" className="kc-body text-sm text-foreground" data-testid="call-unavailable">{data.unavailable.message}</p>
      ) : (
        <>
          <fieldset className="space-y-1" data-testid="call-staff">
            <legend className="sr-only">Team member to ring</legend>
            {data.staff.map((s) => (
              <label key={s.teamMemberId} className="kc-body flex items-center gap-2 text-sm text-foreground">
                <input type="radio" name={`call-staff-${leadId}`} checked={choice === s.teamMemberId} onChange={() => setPicked(s.teamMemberId)} />
                {s.name}
                {s.teamMemberId === data.defaultTeamMemberId && <span className="text-xs text-muted-foreground">(you)</span>}
              </label>
            ))}
          </fieldset>
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" className="kc-save" disabled={!choice || busy} onClick={() => void start()} data-testid="call-start">
              {busy ? 'Starting…' : `Ring and connect me to ${firstName}`}
            </Button>
          </div>
          {demoNote && <p role="status" className="kc-body text-sm text-foreground" data-testid="call-demo-note">{DEMO_CALL_MESSAGE}</p>}
          {error && <p role="alert" className="kc-body text-sm text-destructive" data-testid="call-error">{error}</p>}
        </>
      )}
    </section>
  );
}
